import { useMemo } from 'react';
import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { api } from '../../../../lib/apiClient';
import { FORMULA_PREVIEW_KEY } from '../../keys';
import { SchemaDatapointWithParent } from '../flattenDatapoints';
import { extractVariablesPerQuery } from '../utils/lookup-fields/extractDependenciesPerQuery';
import {
  ARTIFICIAL_VARIABLE_PREFIX,
  makeArtificialVariablesSchema,
} from '../utils/lookup-fields/makeArtificialVariablesSchema';

const VARIABLES_FORMULAS_INFO_KEY = [
  FORMULA_PREVIEW_KEY,
  'lookup-variables-formulas-info',
] as const;

export const useLookupDependenciesByQuery = (
  flatSchema: SchemaDatapointWithParent[],
  currentFormulaId: string,
  formulaType: string | undefined
): string[][] | undefined => {
  const extracted = useMemo(() => {
    if (formulaType !== 'lookup') return undefined;

    return extractVariablesPerQuery(flatSchema, currentFormulaId);
  }, [formulaType, flatSchema, currentFormulaId]);

  const schemaWithVariables = useMemo(() => {
    if (!extracted) return undefined;

    return makeArtificialVariablesSchema(extracted.variables);
  }, [extracted]);

  const variablesFormulasInfoQuery = useQuery({
    queryKey: [...VARIABLES_FORMULAS_INFO_KEY, schemaWithVariables],
    queryFn: () =>
      api.request(
        endpoints.internal.schemas.formulasInfo({
          schemaContent: schemaWithVariables!,
        })
      ),
    enabled: !!schemaWithVariables,
    cacheTime: 0,
  });

  return useMemo(() => {
    if (!extracted) return undefined;

    const formulasInfoResults = variablesFormulasInfoQuery.data?.results;

    if (!formulasInfoResults) return undefined;

    const depsMap = R.pipe(
      formulasInfoResults,
      R.filter(r => r.id.startsWith(ARTIFICIAL_VARIABLE_PREFIX)),
      R.indexBy(r => r.id.slice(ARTIFICIAL_VARIABLE_PREFIX.length)),
      R.mapValues(r => r.dependencies)
    );

    return extracted.variablesPerQuery.map(variableNames =>
      R.pipe(
        variableNames,
        R.flatMap(name => depsMap[name] ?? []),
        R.unique()
      )
    );
  }, [extracted, variablesFormulasInfoQuery.data]);
};
